/**
 * The Expertise loop graphic's geometry and timing, ported from the
 * prototype's expertise.js and expertise-halo.js. Coordinates are in the svg's
 * 1440 x 1194.2 viewBox.
 */

/** The central vertical line every loop is tangent to */
export const AXIS = 720;
/** The band's centre */
export const CY = 740.2;
/** Every loop's half-height */
export const RY = 340;
export const VIEWBOX_WIDTH = 1440;
export const VIEWBOX_HEIGHT = 1194.2;

/** Left to right, which is also opening order */
export const LOOPS = [
    { side: -1, rx: 340 },
    { side: -1, rx: 170 },
    { side: 1, rx: 170 },
    { side: 1, rx: 340 },
] as const;

/** An ellipse with rx 0 doesn't render, so keep a sliver */
const HAIRLINE = 0.75;
/** How much of its opening a loop takes to reach full opacity */
const OPEN_AT = 0.3;

/** The seed line registers before anything opens */
export const HOLD = 140;
/** One loop's own opening */
export const DUR = 560;
export const STAGGER = 175;
/** 1225ms end to end */
export const TOTAL = HOLD + STAGGER * 3 + DUR;
/** An area's copy comes in halfway through its own loop's opening */
export const AREA_AT = 0.5;

export const smooth = (x: number) =>
    x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x);

/** How far a loop has opened when the next one is pulled off it */
const LINK = smooth(STAGGER / DUR);

/**
 * Where each loop's half-width starts: loop 0 is the seed on the axis, and
 * every later loop is born on the live line of the one before it.
 */
export const START_RX = [0, LOOPS[0].rx * LINK, 0, LOOPS[2].rx * LINK];

/** How far loop i has opened, 0..1, at `elapsed` ms into the entrance */
export const progress = (i: number, elapsed: number) =>
    smooth((elapsed - HOLD - i * STAGGER) / DUR);

/** The centre that keeps a loop's near edge on the axis */
export const centreOf = (i: number, rx: number) => AXIS + LOOPS[i].side * rx;

export interface LoopShape {
    rx: number;
    cx: number;
    opacity: number;
}

/** Loop i's ellipse at opening progress p */
export const loopShape = (i: number, p: number): LoopShape => {
    const rx = Math.max(
        HAIRLINE,
        START_RX[i] + (LOOPS[i].rx - START_RX[i]) * p,
    );
    return {
        rx,
        cx: centreOf(i, rx),
        // The seed is drawn from the first frame; the others fade up while
        // they still sit on the line they were pulled from
        opacity: i === 0 ? 1 : smooth(p / OPEN_AT),
    };
};

/** How many areas' copy is showing at `elapsed` ms */
export const areasOpened = (elapsed: number) =>
    LOOPS.filter((_, i) => progress(i, elapsed) > AREA_AT).length;

/* ---------------------------------------------------------- travelling dots */

/** One circuit, in ms */
const LAP_MIN = 11538;
const LAP_MAX = 20000;
/** Clear air a dot's arrival at the seam keeps from any other dot's */
const SEAM_GAP = 2600;
const TAU = Math.PI * 2;

export interface Lap {
    /** Which loop */
    i: number;
    /** Fixed per dot, never per lap */
    dir: 1 | -1;
    dur: number;
    t0: number;
    /** When it next reaches the seam */
    arrive: number;
}

/** Two of the three dots run one way, the odd one the other */
export const createLaps = (count: number): Lap[] =>
    Array.from({ length: count }, (_, k) => ({
        i: 0,
        dir: k % 2 ? -1 : 1,
        dur: LAP_MIN,
        t0: 0,
        arrive: 0,
    }));

/**
 * Starts a dot's next lap: on a wire no other dot is on where there is one,
 * with a length chosen so it reaches the seam clear of the others. The first
 * lap is backdated so the dot is already part-way round.
 */
export const newLap = (
    lap: Lap,
    laps: Lap[],
    now: number,
    first: boolean,
    random: () => number = Math.random,
) => {
    const taken = laps.filter((o) => o !== lap).map((o) => o.i);
    const free = LOOPS.map((_, k) => k).filter((k) => !taken.includes(k));
    const pool = free.length ? free : LOOPS.map((_, k) => k);
    lap.i = pool[Math.floor(random() * pool.length)];

    let bestDur = LAP_MIN;
    let bestScore = -Infinity;
    for (let n = 0; n < 24; n++) {
        const dur = LAP_MIN + random() * (LAP_MAX - LAP_MIN);
        const arrive = now + dur;
        let score = Infinity;
        for (const other of laps) {
            if (other === lap) continue;
            const a = other.arrive - SEAM_GAP;
            const b = other.arrive + SEAM_GAP;
            const d =
                arrive < a
                    ? a - arrive
                    : arrive > b
                      ? arrive - b
                      : -Math.min(arrive - a, b - arrive);
            if (d < score) score = d;
        }
        if (score > bestScore) {
            bestScore = score;
            bestDur = dur;
        }
        if (score > 0) break;
    }
    lap.dur = bestDur;
    lap.t0 = first ? now - random() * lap.dur * 0.9 : now;
    lap.arrive = lap.t0 + lap.dur;
};

/** A dot's position at `now`, starting its next lap when this one is over */
export const dotPosition = (
    lap: Lap,
    laps: Lap[],
    now: number,
    random: () => number = Math.random,
) => {
    let s = (now - lap.t0) / lap.dur;
    if (s >= 1) {
        newLap(lap, laps, now, false, random);
        s = 0;
    }
    // Linear: easing stopped the dots dead on the seam all four loops share
    const phi = TAU * Math.max(0, Math.min(1, s)) * lap.dir;
    const loop = LOOPS[lap.i];
    const theta = (loop.side < 0 ? 0 : Math.PI) + phi;
    return {
        x: centreOf(lap.i, loop.rx) + loop.rx * Math.cos(theta),
        y: CY + RY * Math.sin(theta),
    };
};

/* -------------------------------------------------------------------- halo */

/** config.js EXPERTISE */
export const HALO_COLOURS = {
    core: "#f5381a",
    warm: "#f0421a",
    mid: "#eb501b",
    tail: "#e7521d",
} as const;
const HALO_ALPHA = 0.8;

/** Offset, colour and opacity as a fraction of the peak */
const RAMP: readonly [number, keyof typeof HALO_COLOURS, number][] = [
    [0.0, "core", 1.0],
    [0.13, "warm", 0.9514],
    [0.26, "mid", 0.8502],
    [0.39, "mid", 0.6883],
    [0.5, "mid", 0.5263],
    [0.6, "tail", 0.3846],
    [0.7, "tail", 0.2632],
    [0.8, "tail", 0.1619],
    [0.88, "tail", 0.0911],
    [0.94, "tail", 0.0405],
    [1.0, "tail", 0.0],
];

export const HALO_STOPS = RAMP.map(([offset, colour, fraction]) => ({
    offset,
    colour: HALO_COLOURS[colour],
    opacity: Number((fraction * HALO_ALPHA).toFixed(3)),
}));

export const HALO_RADIUS = 380;
/** On a phone the halo is pinned on the figure's centre and this much larger */
export const HALO_RADIUS_PHONE = HALO_RADIUS * 2.02;
/** How far either side of the axis the mass may wander */
const REACH = 400;
/** And above and below the band's centre */
const RISE = RY * 0.38;
/** Pointer lag per 60Hz frame */
const EASE = 0.045;
/** It only leans this fraction of the way toward the pointer */
const PULL = 0.62;
const PULL_Y = 0.42;

const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);

export interface HaloState {
    /** Where the pointer is, in viewBox units */
    tx: number;
    ty: number;
    /** Where the mass is */
    cx: number;
    cy: number;
}

export const createHalo = (): HaloState => ({
    tx: AXIS,
    ty: CY,
    cx: AXIS,
    cy: CY,
});

/**
 * Moves the mass one frame toward the pointer and returns the clamped centre.
 * The prototype eases by a fixed step per frame; this scales it by `dt` so a
 * 120Hz display isn't twice as fast (as Foundation's glow does).
 */
export const stepHalo = (state: HaloState, dt: number) => {
    const k = 1 - Math.pow(1 - EASE, dt * 60);
    state.cx += (AXIS + (state.tx - AXIS) * PULL - state.cx) * k;
    state.cy += (CY + (state.ty - CY) * PULL_Y - state.cy) * k;
    return {
        cx: clamp(state.cx, AXIS - REACH, AXIS + REACH),
        cy: clamp(state.cy, CY - RISE, CY + RISE),
    };
};
